import { ComponentRef, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import { useSurveyPaths } from '../../../hooks/useSurveyPaths';
import { Routes } from '../../../enums/Routes';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Button,
  ErrorSummary,
  Heading,
  Link,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { WorkEnvironmentArchive } from '../../../api/workEnvironmentTypes';
import { useArchiveEvaluatedSurveyMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { useShareWithTeamMutation } from '../../../hooks/queries/useTeamSharesQuery';
import { formatDay } from '../../HRDashboard/hmsFormat';
import {
  checkLabel,
  CHECKLIST_ID,
  checkNote,
  missingForArchive,
  missingShares,
} from '../archiveView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import { RESIDUAL_SECTION_ID } from './ResidualRiskCard';
import { SignatureActions } from './SignatureActions';
import classes from './Evaluation.module.css';

type Props = {
  surveyId: number;
  archive: WorkEnvironmentArchive;
  onShowDocumentation: () => void;
};

const MissingSummary = ({
  items,
}: {
  items: ReturnType<typeof missingForArchive>;
}): ReactElement => {
  const { t } = useTranslation();
  const ref = useRef<ComponentRef<typeof ErrorSummary>>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <ErrorSummary ref={ref}>
      <ErrorSummary.Heading>
        {t('workEnvironmentArchive.checklist.errorTitle')}
      </ErrorSummary.Heading>
      <ErrorSummary.List>
        {items.map((item) => (
          <ErrorSummary.Item key={item.key}>
            <ErrorSummary.Link href={item.href}>{item.text}</ErrorSummary.Link>
          </ErrorSummary.Item>
        ))}
      </ErrorSummary.List>
    </ErrorSummary>
  );
};

/** What the documentation needs, and the approval that archives the work */
export const ArchiveCard = ({
  surveyId,
  archive,
  onShowDocumentation,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const paths = useSurveyPaths();
  const { may } = useSurveyActions();
  const [tried, setTried] = useState(false);
  const archiveWork = useArchiveEvaluatedSurveyMutation(surveyId);
  const share = useShareWithTeamMutation(surveyId);
  const missing = missingForArchive(archive, t, i18n.language);
  const unshared = missingShares(archive.summary);

  const approve = () => {
    setTried(true);
    if (missing.length === 0) archiveWork.mutate(undefined);
  };
  const sendToTeam = () =>
    Promise.all(unshared.map((kind) => share.mutateAsync(kind))).catch(
      () => undefined,
    );

  return (
    <section
      id={CHECKLIST_ID}
      aria-labelledby="archive-heading"
      className={`${process.card} ${process.sideCard}`}
    >
      <div className={process.sideHeading}>
        <Heading level={2} data-size="xs" id="archive-heading">
          {t('workEnvironmentArchive.checklist.title')}
        </Heading>
        <Paragraph data-size="sm" className={process.subtle}>
          {t('workEnvironmentArchive.checklist.intro')}
        </Paragraph>
      </div>
      {archive.archived && (
        <Alert data-color="success" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentArchive.checklist.archived', {
              date: formatDay(archive.archived.at, t, i18n.language),
            })}
          </Paragraph>
        </Alert>
      )}
      {archive.nextRound && (
        <Alert data-color="info" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentArchive.checklist.nextRound')}
          </Paragraph>
          <Paragraph data-size="sm">
            <Link asChild>
              <RouterLink to={paths.survey(archive.nextRound.surveyId)}>
                {t('workEnvironmentArchive.checklist.toNextRound')}
              </RouterLink>
            </Link>
          </Paragraph>
        </Alert>
      )}
      {tried && missing.length > 0 && <MissingSummary items={missing} />}
      <ul className={classes.checklist}>
        {archive.checklist.map((check) => (
          <li key={check.key} className={classes.check}>
            <span
              aria-hidden
              className={classes.checkMark}
              data-done={check.done}
            >
              {check.done ? '✓' : '!'}
            </span>
            <span className={classes.checkText}>
              <span className="ds-label" data-size="sm">
                {checkLabel(check, archive.summary, t, archive.safetyRepMissing)}
                <span className="ds-sr-only">
                  {' – '}
                  {t(
                    check.done
                      ? 'workEnvironmentArchive.checklist.done'
                      : 'workEnvironmentArchive.checklist.missing',
                  )}
                </span>
              </span>
              <span
                className={`ds-paragraph ${classes.checkNote}`}
                data-size="xs"
                data-done={check.done}
              >
                {checkNote(check, archive, t, i18n.language)}
              </span>
              {(archive.canRequestSignature ?? archive.canArchive) &&
                may('requestSignature') &&
                check.key === 'safetyRep' && (
                  <SignatureActions
                    surveyId={surveyId}
                    signature={archive.summary.signature}
                    safetyRep={archive}
                  />
                )}
              {/* The safety representative signs in their own part of the app */}
              {/* Only for the one who acts as safety representative: never the team leader of the team */}
              {paths.section === 'safetyRep' &&
                may('reviewPlan') &&
                check.key === 'safetyRep' &&
                archive.summary.signature?.status === 'REQUESTED' && (
                  <span>
                    <Link asChild data-size="sm">
                      <RouterLink to={Routes.SafetyRepSigning}>
                        {t('workEnvironmentArchive.checklist.actions.sign')}
                      </RouterLink>
                    </Link>
                  </span>
                )}
              {archive.canArchive &&
                may('share') &&
                check.key === 'shared' &&
                !check.done && (
                  <span>
                    <Button
                      variant="tertiary"
                      className={classes.inlineAction}
                      data-size="sm"
                      loading={share.isPending}
                      onClick={sendToTeam}
                    >
                      {t('workEnvironmentArchive.checklist.actions.shared')}
                    </Button>
                  </span>
                )}
              {check.key === 'shared' && share.isError && (
                <ValidationMessage data-size="sm">
                  {t('workEnvironmentArchive.checklist.sendFailed')}
                </ValidationMessage>
              )}
              {(archive.canDecideResidual ?? archive.canArchive) &&
                may('decideResidual') &&
                check.key === 'residual' &&
                !check.done && (
                  <span>
                    <Link href={`#${RESIDUAL_SECTION_ID}`} data-size="sm">
                      {t('workEnvironmentArchive.checklist.actions.residual')}
                    </Link>
                  </span>
                )}
            </span>
          </li>
        ))}
      </ul>
      {archiveWork.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentArchive.checklist.archiveFailed')}
          </Paragraph>
        </Alert>
      )}
      <div className={classes.archiveActions}>
        {archive.canArchive && may('archive') && (
          <Button loading={archiveWork.isPending} onClick={approve}>
            {t('workEnvironmentArchive.checklist.archive')}
          </Button>
        )}
        <Button variant="secondary" onClick={onShowDocumentation}>
          {t('workEnvironmentArchive.checklist.toDocumentation')}
        </Button>
        <Button asChild variant="secondary">
          <RouterLink to={paths.report(surveyId)}>
            {t('workEnvironmentReport.card.open')}
          </RouterLink>
        </Button>
      </div>
    </section>
  );
};
